import { ArrowLeft, Loader2, UserPen } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { useCaseSchema } from '@/hooks/use-case-queries'
import type { DuplicateCandidate } from '@/services/api/manual-graph'
import type { EntityType } from '@/services/api/schemas'
import type { GraphViewNode } from '@/types/graph-view'
import { DuplicateCandidates } from './DuplicateCandidates'
import { EntityPropertyFields, type FieldMessages } from './EntityPropertyFields'
import { EntityTypeGlyph } from './EntityTypeGlyph'
import { EntityTypePicker } from './EntityTypePicker'
import {
  creatableEntityTypes,
  entityFields,
  entityTypeName,
  type FormValues,
  hasLookupValue,
  toRequestProperties,
  validateEntityForm,
} from './entity-form'
import { refusalOf, useCreateManualEntity, useEntityDuplicates } from './hooks/useManualGraph'
import { SchemaGapNotice } from './SchemaGapNotice'

export interface CreateEntityDialogProps {
  caseId: string
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The entity as written; the caller adds it to its view. */
  onCreated?: (entity: GraphViewNode) => void
  /** Show an existing entity the duplicate check found, instead of creating one. */
  onShowExisting?: (candidate: DuplicateCandidate) => void
  /** Offered in the success toast, e.g. from the Research page. */
  onShowInGraph?: (entity: GraphViewNode) => void
}

/**
 * Add an entity by hand: pick a type from the case schema, fill in what is
 * known, create. A type the schema lacks is added in the Schema Builder first;
 * the entity is stamped as created manually, by the signed-in user.
 */
export function CreateEntityDialog(props: CreateEntityDialogProps) {
  return (
    <Dialog open={props.open} onOpenChange={props.onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        {/* Mounted only while open: every opening starts at the type picker,
            and the schema is read only when someone is adding an entity. */}
        <CreateEntityBody {...props} />
      </DialogContent>
    </Dialog>
  )
}

function CreateEntityBody(props: CreateEntityDialogProps) {
  const { caseId } = props
  const { t } = useTranslation()
  const schemaQ = useCaseSchema(caseId)
  const types = useMemo(() => creatableEntityTypes(schemaQ.data), [schemaQ.data])
  const [picked, setPicked] = useState<EntityType | null>(null)

  return (
    <>
      <DialogHeader>
        <DialogTitle>{t('manualGraph.createEntity.title')}</DialogTitle>
        <DialogDescription>
          {picked
            ? t('manualGraph.createEntity.formDescription')
            : t('manualGraph.createEntity.pickTypeDescription')}
        </DialogDescription>
      </DialogHeader>
      {schemaQ.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="size-5 animate-spin text-muted-foreground" aria-hidden />
        </div>
      ) : !schemaQ.data || types.length === 0 ? (
        <SchemaGapNotice caseId={caseId} gap={schemaQ.data ? 'noTypes' : 'noSchema'} />
      ) : picked ? (
        <EntityFormStep {...props} entityType={picked} onBack={() => setPicked(null)} />
      ) : (
        <div className="flex min-w-0 flex-col gap-4">
          <EntityTypePicker types={types} onPick={setPicked} />
          <SchemaGapNotice caseId={caseId} gap="missingType" />
        </div>
      )}
    </>
  )
}

interface EntityFormStepProps extends CreateEntityDialogProps {
  entityType: EntityType
  onBack: () => void
}

function EntityFormStep({ entityType, onBack, ...props }: EntityFormStepProps) {
  const { caseId, onOpenChange, onShowExisting } = props
  const { t, i18n } = useTranslation()
  const fields = useMemo(() => entityFields(entityType), [entityType])
  // The duplicate lookup reads the name and the unique values only: editing any
  // other field must not send it again.
  const lookupFields = useMemo(() => fields.filter(f => f.isDisplayName || f.unique), [fields])
  const typeName = entityTypeName(entityType, i18n.language)
  const [values, setValues] = useState<FormValues>({})
  const [errors, setErrors] = useState<FieldMessages>({})
  const [refusal, setRefusal] = useState<string | null>(null)
  const createEntity = useCreateManualEntity(caseId)
  const lookup = hasLookupValue(lookupFields, values)
    ? { type: entityType.label, properties: toRequestProperties(lookupFields, values) }
    : null
  const duplicates = useEntityDuplicates(caseId, lookup)

  const setValue = (name: string, value: string | boolean | undefined) => {
    setValues(current => ({ ...current, [name]: value }))
    setErrors(({ [name]: _cleared, ...rest }) => rest)
  }

  const submit = async () => {
    const local = messagesFor(validateEntityForm(fields, values), t)
    setErrors(local)
    setRefusal(null)
    if (Object.keys(local).length > 0) return
    try {
      const entity = await createEntity.mutateAsync({
        type: entityType.label,
        properties: toRequestProperties(fields, values),
      })
      announceCreated(entity, typeName, props, t)
      onOpenChange(false)
    } catch (error) {
      const refused = refusalOf(error)
      if (!refused) {
        toast.error(t('manualGraph.createEntity.failed'))
        return
      }
      setErrors(messagesFor(refused.fields, t))
      setRefusal(refused.message)
    }
  }

  return (
    <form
      className="flex min-w-0 flex-col gap-4"
      onSubmit={event => {
        event.preventDefault()
        void submit()
      }}
    >
      <ChosenType entityType={entityType} name={typeName} onBack={onBack} />
      {refusal && (
        <p
          role="alert"
          className="rounded-lg border border-destructive-border bg-destructive-surface px-3 py-2 text-sm text-destructive-foreground"
        >
          {refusal}
        </p>
      )}
      <EntityPropertyFields fields={fields} values={values} errors={errors} onChange={setValue} />
      <DuplicateCandidates
        candidates={lookup ? (duplicates.data ?? []) : []}
        onShow={
          onShowExisting
            ? candidate => {
                onShowExisting(candidate)
                onOpenChange(false)
              }
            : undefined
        }
      />
      <FormFooter
        typeName={typeName}
        pending={createEntity.isPending}
        onCancel={() => onOpenChange(false)}
      />
    </form>
  )
}

function ChosenType({
  entityType,
  name,
  onBack,
}: {
  entityType: EntityType
  name: string
  onBack: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex min-w-0 items-center gap-3 rounded-lg border border-border bg-muted/40 px-3 py-2">
      <EntityTypeGlyph label={entityType.label} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-medium text-foreground">{name}</p>
        {entityType.description && (
          <p className="truncate text-xs text-muted-foreground">{entityType.description}</p>
        )}
      </div>
      <Button type="button" variant="ghost" size="xs" onClick={onBack}>
        <ArrowLeft aria-hidden />
        {t('manualGraph.createEntity.changeType')}
      </Button>
    </div>
  )
}

function FormFooter({
  typeName,
  pending,
  onCancel,
}: {
  typeName: string
  pending: boolean
  onCancel: () => void
}) {
  const { t } = useTranslation()
  return (
    <DialogFooter className="items-center gap-3 sm:justify-between">
      <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
        <UserPen className="size-3.5 shrink-0" aria-hidden />
        {t('manualGraph.createEntity.provenance')}
      </p>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="solid" loading={pending} disabled={pending}>
          {pending
            ? t('manualGraph.createEntity.creating')
            : t('manualGraph.createEntity.create', { type: typeName })}
        </Button>
      </div>
    </DialogFooter>
  )
}

type Translate = ReturnType<typeof useTranslation>['t']

/** Error codes (local or the server's) in words, by field. */
function messagesFor(codes: Record<string, string>, t: Translate): FieldMessages {
  return Object.fromEntries(
    Object.entries(codes).map(([name, code]) => [
      name,
      t(`manualGraph.errors.${code}`, { defaultValue: t('manualGraph.errors.invalid_type') }),
    ])
  )
}

function announceCreated(
  entity: GraphViewNode,
  typeName: string,
  { onCreated, onShowInGraph }: CreateEntityDialogProps,
  t: Translate
) {
  onCreated?.(entity)
  toast.success(
    t('manualGraph.createEntity.created', { type: typeName, name: entity.name ?? entity.id }),
    onShowInGraph
      ? {
          action: {
            label: t('manualGraph.createEntity.showInGraph'),
            onClick: () => onShowInGraph(entity),
          },
        }
      : undefined
  )
}
